import { exportDataGrid } from 'devextreme/excel_exporter';
import type dxDataGrid from 'devextreme/ui/data_grid';
import type { Column } from 'devextreme/ui/data_grid';
import saveAs from 'file-saver';

function escapeCsvCell(value: unknown): string {
  if (value === null || value === undefined) return '';
  const text = value instanceof Date ? value.toISOString() : String(value);
  if (/[",\n\r]/.test(text)) return `"${text.replace(/"/g, '""')}"`;
  return text;
}

function exportableColumns(grid: dxDataGrid): Column[] {
  return grid
    .getVisibleColumns()
    .filter((col) => {
      if (col.type === 'buttons' || col.type === 'detailExpand' || col.type === 'adaptive') return false;
      const field = col.dataField;
      return field != null && String(field).length > 0;
    });
}

export async function exportOpsGridExcel(
  grid: dxDataGrid | undefined,
  fileName: string,
  worksheetName: string
) {
  if (!grid) return;

  // Load ExcelJS only when the user exports. The library's minified dist uses
  // direct eval for formula parsing; keeping it off the critical path avoids
  // shipping that to every grid page.
  const { Workbook } = await import('exceljs');
  const workbook = new Workbook();
  const worksheet = workbook.addWorksheet(worksheetName);
  await exportDataGrid({
    component: grid,
    worksheet,
    autoFilterEnabled: true
  });
  const buffer = await workbook.xlsx.writeBuffer();
  saveAs(new Blob([buffer], { type: 'application/octet-stream' }), `${fileName}.xlsx`);
}

/** Exports visible columns and currently loaded/filtered data rows as CSV. */
export function exportOpsGridCsv(grid: dxDataGrid | undefined, fileName: string) {
  if (!grid) return;

  const columns = exportableColumns(grid);
  if (columns.length === 0) return;

  const headers = columns.map((col) => col.caption || String(col.dataField));
  const lines = [headers.map(escapeCsvCell).join(',')];

  const dataRows = grid.getVisibleRows().filter((row) => row.rowType === 'data');
  for (const row of dataRows) {
    const rowIndex = row.rowIndex;
    if (rowIndex === undefined) continue;
    const cells = columns.map((col) => {
      const field = String(col.dataField);
      const value = grid.cellValue(rowIndex, field);
      return escapeCsvCell(value);
    });
    lines.push(cells.join(','));
  }

  const blob = new Blob([lines.join('\r\n')], { type: 'text/csv;charset=utf-8' });
  saveAs(blob, `${fileName}.csv`);
}
